import { format, isValid, parseISO } from "date-fns";

export const getCurrentTableEntries = (
  limit: number,
  pageno: number
): {
  lowerLimit: number;
  higherLimit: number;
} => {
  const higherLimit = pageno * limit;
  const lowerLimit = higherLimit - limit + 1;
  return {
    lowerLimit,
    higherLimit,
  };
};

export const isBlobURL = (url: string) => {
  // Use URL constructor to parse the URL
  const urlObject = new URL(url);

  // Check if the protocol is 'blob:'
  return urlObject.protocol === "blob:";
};

export const formatNumberWithSuffix = (value: number): string => {
  if (typeof value !== "number") return "0";

  if (value < 1000) {
    return value.toString();
  } else if (value < 1000000) {
    const formattedNumber = value / 1000;
    const isDecimal = formattedNumber % 1 !== 0;
    return formattedNumber.toFixed(isDecimal ? 2 : 0) + "K";
  } else if (value < 1000000000) {
    const formattedNumber = value / 1000000;
    const isDecimal = formattedNumber % 1 !== 0;
    return formattedNumber.toFixed(isDecimal ? 2 : 0) + "M";
  } else {
    const formattedNumber = value / 1000000000;
    const isDecimal = formattedNumber % 1 !== 0;
    return formattedNumber.toFixed(isDecimal ? 2 : 0) + "B";
  }
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const determineTier = (positions: number) => {
  if (positions <= 10000) {
    return "Tier 1 | Under 10k";
  } else if (positions > 10000 && positions <= 100000) {
    return "Tier 2 | 10,001 - 100k";
  } else if (positions > 100000 && positions <= 300000) {
    return "Tier 3 | 100,001 - 300k";
  } else if (positions > 300000 && positions <= 500000) {
    return "Tier 4 | 300,001 - 500k";
  } else if (positions > 500000) {
    return "Tier 5 | Over 500k";
  } else {
    return "Invalid number of positions";
  }
};

export const generateObjectId = (): string => {
  const timestamp = Math.floor(Date.now() / 1000).toString(16);
  const randomBytes = Array.from({ length: 5 }, () =>
    Math.floor(Math.random() * 256)
  )
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const counter = Math.floor(Math.random() * 0x1000000)
    .toString(16)
    .padStart(6, "0");

  return (timestamp + randomBytes + counter).toLowerCase().slice(0, 24);
};

/**
 * A function to format a Date object as either a date, time, or both
 * @param date - A Date object or ISO string
 * @param formatType - Specifies whether to return "date", "time", or both
 * @returns A formatted string based on the format type
 */
export const formatDateOrTime = (
  dateInput: Date | string,
  formatType: "date" | "time" | "both" = "date"
): string | null => {
  const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;

  if (!isValid(date)) {
    return null;
  }

  switch (formatType) {
    case "date":
      return format(date, "d MMM yyyy");
    case "time":
      return format(date, "h:mm a");
    case "both":
      return format(date, "d MMM yyyy h:mm a");
    default:
      return format(date, "d MMM yyyy");
  }
};
/*
 * Use this function when you want to extract numbers from a string
 * @param string
 * @returns A number
 */

export const extractNumberFromString = (str: string) => {
  return parseFloat(str.replace(/[$,%\s]/g, ""));
};

/**
 * Use this function to add custom symbols to specified keys in an array of objects.
 *
 * @param array - The array of objects to update
 * @param keys - The keys for which the symbol should be added
 * @param symbol - The symbol to prepend to the values
 * @param recursiveKey - The key under which the function should apply recursively (optional)
 * @returns A formatted array
 */
export const concatCustomSymbolsInArray = <T extends Record<string, any>>(
  array: T[],
  keys: string[],
  symbol: string,
  recursiveKey?: string
): T[] => {
  return array.map((item) => {
    // Create a shallow copy of the item, using type assertions to allow safe indexing
    const updatedItem = { ...item } as Record<string, any>;

    keys.forEach((key) => {
      const value = updatedItem[key];
      if (
        value !== undefined &&
        (typeof value === "string" || typeof value === "number")
      ) {
        updatedItem[key] = `${symbol}${value}`;
      }
    });

    // Recursively apply the logic to the nested key if it exists and is an array
    if (recursiveKey && Array.isArray(updatedItem[recursiveKey])) {
      updatedItem[recursiveKey] = concatCustomSymbolsInArray(
        updatedItem[recursiveKey],
        keys,
        symbol,
        recursiveKey
      );
    }

    return updatedItem as T;
  });
};

/**
 * Calculates the total sum of numeric values for a specific field in an array of objects.
 *
 * @param array - The array of objects containing the data.
 * @param fieldName - The field name for which the values should be summed.
 * @returns The sum of the numeric values for the specified field.
 */
export const calculateSumOfValues = <T extends Record<string, any>>(
  array: T[],
  fieldName: string
): number => {
  return array.reduce((prev, curr) => {
    const rawValue = curr[fieldName];
    const fieldValue = extractNumberFromString(rawValue.toString());
    return prev + fieldValue;
  }, 0);
};

/**
 * Helper function to format the value based on field type.
 *
 * @param value - The value to be formatted. Should be convertible to a number.
 * @param symbol - The symbol to append or prepend based on the field.
 * @param position - The position of the symbol (left || right || center)
 * @returns Formatted string or the original value if no formatting is needed.
 */
export const getFormattedValue = (
  value: number | string,
  symbol: string,
  position: "left" | "right" | "center"
): string => {
  // Always prefix currency values with symbol regardless of alignment
  if (symbol === "$") {
    return `${symbol}${value}`;
  }
  switch (position) {
    case "left":
      return `${symbol}${value}`;
    case "right":
      return `${value}${symbol}`;
    case "center":
      return `${symbol}${value}${symbol}`;
    default:
      return value.toString();
  }
};

/**
 * Helper function formatAndCapitalize to format and capitalize the first word of a string.
 * It also replaces underscores with spaces.
 *
 * @param input - The input string to be formatted. May contain underscores or be in uppercase.
 * @returns The formatted string with the first word capitalized and underscores replaced with spaces.
 */
export function formatAndCapitalize(input: string): string {
  if (!input) return "";

  return input
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (char) => char.toUpperCase());
}
export const getRandomNameAndEmail = (
  randomNames: Array<string>,
  randomEmailDomains: Array<string>
) => {
  const randomIndex = Math.floor(Math.random() * randomNames.length);
  const name = randomNames[randomIndex];
  const emailDomain =
    randomEmailDomains[Math.floor(Math.random() * randomEmailDomains.length)];
  const emailPrefix = name.toLowerCase().replace(/\s+/g, ".");
  const email = `${emailPrefix}@${emailDomain}`;

  return { name, email };
};

export const formatFileSize = (size: number): string => {
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const numberWithCommas = (num: number): string => {
  return Math.floor(num)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const getFilenameFromUrl = (url: string): string => {
  const parsedUrl = new URL(url);
  const pathname = parsedUrl.pathname; // Get the path part of the URL
  const filename = pathname.substring(pathname.lastIndexOf("/") + 1); // Extract the filename
  return filename;
};

export const snakeToTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Extracts HTS code from a string that may contain additional information like value data
 * @param htsCodeString - The HTS code string from API (e.g., "4011101050 ($334.0M goods value)" or "4011.10.10.50")
 * @returns The clean HTS code without additional text or dots
 */
export const extractHTSCode = (htsCodeString: string): string => {
  if (!htsCodeString) return "";
  
  // First, extract the part before any space or parenthesis
  const match = htsCodeString.match(/^([^\s(]+)/);
  const extracted = match ? match[1] : htsCodeString;
  
  // Then remove any dots to get pure numeric code
  return extracted.replace(/\./g, "");
};

/**
 * Formats an HTS code with proper dots based on its length
 * @param htsCode - The HTS code to format (e.g., "4011101050")
 * @returns Formatted HTS code (e.g., "4011.10.10.50")
 */
export const formatHTSCode = (htsCode: string): string => {
  if (!htsCode) return "";
  
  // Remove any non-numeric characters first
  const cleanCode = htsCode.replace(/\D/g, "");
  
  // Format based on length
  if (cleanCode.length <= 4) {
    return cleanCode;
  } else if (cleanCode.length <= 6) {
    return `${cleanCode.slice(0, 4)}.${cleanCode.slice(4)}`;
  } else if (cleanCode.length <= 8) {
    return `${cleanCode.slice(0, 4)}.${cleanCode.slice(4, 6)}.${cleanCode.slice(6)}`;
  } else {
    return `${cleanCode.slice(0, 4)}.${cleanCode.slice(4, 6)}.${cleanCode.slice(6, 8)}.${cleanCode.slice(8)}`;
  }
};

/**
 * Extracts country code from a string that may contain additional information
 * @param countryString - The country string from API (e.g., "TH (99.2% of goods value)")
 * @returns The clean 2-letter country code
 */
export const extractCountryCode = (countryString: string): string => {
  if (!countryString) return "";
  
  // Extract the first 2 uppercase letters which should be the country code
  const match = countryString.match(/^([A-Z]{2})/);
  return match ? match[1] : countryString;
};

export const handleCSVDownload = (headers: string[], csvData: any[], filename = "trade_data.csv") => {
  try {
    const escapeCell = (value: unknown) => {
      if (value === null || value === undefined) return "";
      const str = String(value).replace(/"/g, '""');
      return `"${str}"`;
    };

    const headerLine = headers.map(escapeCell).join(",");
    const dataLines = csvData.map((row) => {
      const cells = Array.isArray(row) ? row : Object.values(row);
      return cells.map(escapeCell).join(",");
    });

    const csvContent = [headerLine, ...dataLines].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.log("error", error);
  }
};

export const formatCurrency = (value: number | undefined | null): string => {
  if (value == null || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatCurrencyPrecise = (value: number | undefined | null): string => {
  if (value == null || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};